import type { FunctionComponent } from 'react'
import { Link as RouterLink } from 'react-router'
import styled, { css } from 'styled-components'
import navigationLinks, { glossaryMenuItem } from './navigationLinks'
import {
  HEADER_LINK_CONTACT,
  HEADER_LINK_HELP,
  HEADER_LINKS_ABOUT,
} from '../../config/content-links'
import { isAuthenticated } from '../../utils/auth/auth'
import { routing } from '../../../routes'
import Login from './Login'
import Logout from './Logout'
import Hidden from '../Theme/Hidden'
import { Breakpoint } from '../../hooks/useBreakpoint'
import Link from '../Links/Link/Link'
import Heading from '../Heading/Heading'
import { GLOSSARY, isFeatureEnabled } from '../../../features'

const StyledContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: calc(100% - var(--header-menu-height));
  overflow: scroll;
  z-index: 999;
  background: var(--light);
  padding: 1.25rem 1rem;

  @media screen and (min-width: 64rem) {
    flex-direction: row;
    padding: 3.5rem 2.25rem;
  }
`

const ContainerBackground = styled.div`
  position: absolute;
  top: var(--header-menu-height);
  left: 0;
  right: 0;
  width: auto;
  height: calc(100vh - var(--header-menu-height));
  background: rgba(0, 0, 0, 0.5);
`

const StyledTitle = styled(Heading)`
  display: flex;
  align-items: center;
  width: 100%;
  color: var(--dark);
  font-size: 1.375;
  line-height: 1.4;
  font-weight: 800;
  margin: 1.25rem 0 0.5rem;

  @media screen and (min-width: 48rem) {
    font-size: 2rem;
    line-height: 1.2;
    margin: 1.25rem 0;
  }
`

const StyledLink = styled(Link)<{ $fontWeight?: number }>`
  display: flex;
  align-items: center;
  width: 100%;
  color: var(--blue);
  font-size: 1.125rem;
  line-height: 2;
  font-weight: 400;
  text-align: left;
  text-decoration: none;

  @media screen and (min-width: 48rem) {
    font-size: 1.5rem;
    line-height: 2;
    text-underline-offset: 4px;
  }

  &:hover,
  &:focus {
    text-decoration: underline;
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;
    color: var(--blue);

    svg path {
      fill: var(--blue);
    }
  }

  ${({ $fontWeight }) =>
    $fontWeight &&
    css`
      font-weight: ${$fontWeight};
      &:hover,
      &:focus {
        text-decoration-thickness: 2px;
      }
    `}
`

const StyledChildLinks = styled.div`
  display: flex;
  flex-direction: column;
`

const Wrapper = styled.div`
  width: 100%;
  padding-right: 1rem;
  @media screen and (min-width: 64rem) {
    width: 33.33%;
  }
`

interface HeaderNavLinksProps {
  toggleMenu: () => void
  isMobile?: boolean
}

const HeaderNavLinks: FunctionComponent<HeaderNavLinksProps> = ({ toggleMenu, isMobile }) => {
  const isGlossaryEnabled = isFeatureEnabled(GLOSSARY)
  const mainNavigationLinks = isGlossaryEnabled
    ? [...navigationLinks, glossaryMenuItem]
    : navigationLinks
  return (
    <ContainerBackground onClick={() => !isMobile && toggleMenu()}>
      <StyledContainer
        aria-labelledby="headerNavButton"
        data-testid={isMobile ? 'mobileHeaderNavLinks' : 'headerNavLinks'}
      >
        <Wrapper>
          <Hidden minBreakpoint={Breakpoint.Md}>
            <div>
              {!isAuthenticated() ? <Login /> : <Logout isExpanded toggleMenu={() => null} />}
            </div>
          </Hidden>
          <StyledTitle type="h3">Onderdelen</StyledTitle>
          <StyledChildLinks>
            {mainNavigationLinks.map(({ id, title, to, testId }) => (
              <StyledLink
                $fontWeight={700}
                onClick={toggleMenu}
                as={RouterLink}
                key={id}
                to={to}
                data-testid={testId}
              >
                <span>{title}</span>
              </StyledLink>
            ))}
          </StyledChildLinks>
        </Wrapper>
        <Wrapper>
          <StyledTitle type="h3">Over Ons</StyledTitle>
          <StyledChildLinks>
            {HEADER_LINKS_ABOUT.map(({ title, slug, testId }) => (
              <StyledLink
                onClick={toggleMenu}
                as={RouterLink}
                to={slug}
                data-testid={testId}
                key={testId}
              >
                <span>{title}</span>
              </StyledLink>
            ))}
          </StyledChildLinks>
        </Wrapper>
        <Wrapper>
          <StyledTitle type="h3">Help</StyledTitle>
          <StyledLink
            onClick={toggleMenu}
            as={RouterLink}
            to={routing.help.path}
            data-testid={HEADER_LINK_HELP.testId}
          >
            {HEADER_LINK_HELP.title}
          </StyledLink>
          <StyledLink
            onClick={toggleMenu}
            as={RouterLink}
            to={HEADER_LINK_CONTACT.slug}
            data-testid={HEADER_LINK_CONTACT.testId}
            key={HEADER_LINK_CONTACT.testId}
          >
            <span>{HEADER_LINK_CONTACT.title}</span>
          </StyledLink>
        </Wrapper>
      </StyledContainer>
    </ContainerBackground>
  )
}

export default HeaderNavLinks
